/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\ColorsTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Account Menu module.
----------------------------------------------------------------*/


import React from 'react';
import { Palette, Square } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { colorPresets } from '../utils';
import type { AppearanceForm } from '../types';
import type { UseFormSetValue } from 'react-hook-form';

interface ColorsTabProps {
  watchedValues: AppearanceForm;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: any;
  setValue: UseFormSetValue<AppearanceForm>;
  applyColorPreset: (preset: { name: string; primary: string; secondary: string }) => void;
}

function isPreset(values: AppearanceForm) {
  return colorPresets.some(
    (p) =>
      p.primary.toLowerCase() === values.primaryColor?.toLowerCase() &&
      p.secondary.toLowerCase() === values.secondaryColor?.toLowerCase()
  );
}

export function ColorsTab({ watchedValues, setValue, applyColorPreset }: ColorsTabProps) {
  const br = watchedValues.borderRadius ?? 0.5;
  const customActive = !isPreset(watchedValues);
  const primaryColor = watchedValues.primaryColor || '#3B82F6';
  const secondaryColor = watchedValues.secondaryColor || '#64748B';

  return (
    <div className="space-y-3">
      {/* -- Color Presets + Custom (6 cards unified) -------- */}
      <FormBox title="Color Presets" icon={<Palette />}>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {/* Preset cards */}
          {colorPresets.map((preset) => {
            const isActive =
              primaryColor.toLowerCase() === preset.primary.toLowerCase() &&
              secondaryColor.toLowerCase() === preset.secondary.toLowerCase();
            return (
              <button
                key={preset.name}
                type="button"
                onClick={() => applyColorPreset(preset)}
                className={`p-3 border-2 rounded-lg transition-all text-left ${isActive
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50 hover:bg-accent/30'
                  }`}
              >
                <div className="flex items-center gap-1 mb-2">
                  <div className="w-6 h-6 rounded-sm shadow-sm" style={{ backgroundColor: preset.primary }} />
                  <div className="w-5 h-5 rounded-sm shadow-sm opacity-70" style={{ backgroundColor: preset.secondary }} />
                </div>
                <div className="text-xs font-semibold text-foreground">{preset.name}</div>
                {isActive && (
                  <div className="text-xs font-medium" style={{ color: preset.primary }}>Active</div>
                )}
              </button>
            );
          })}

          {/* Custom card � 6th slot: inline color pickers inside */}
          <div
            className={`p-3 border-2 rounded-lg transition-all ${customActive
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-primary/50 hover:bg-accent/30'
              }`}
          >
            <div className="flex items-center gap-1 mb-2">
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => setValue('primaryColor', e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                title="Pick Primary Color"
                className="w-6 h-6 rounded-sm shadow-sm cursor-pointer border-0 p-0"
                style={{ backgroundColor: primaryColor }}
              />
              <input
                type="color"
                value={secondaryColor}
                onChange={(e) => setValue('secondaryColor', e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                title="Pick Secondary Color"
                className="w-5 h-5 rounded-sm shadow-sm cursor-pointer border-0 p-0 opacity-80"
                style={{ backgroundColor: secondaryColor }}
              />
            </div>
            <div className="text-xs font-semibold text-foreground">Custom</div>
            {customActive && (
              <div className="text-xs font-medium" style={{ color: primaryColor }}>Active</div>
            )}
          </div>
        </div>

        {/* Live color preview */}
        <div className="mt-4 flex items-center gap-3 pt-3 border-t border-border">
          <span className="text-xs font-medium text-muted-foreground flex-shrink-0">Preview</span>
          <div className="w-8 h-8 rounded-md border border-border" style={{ backgroundColor: primaryColor }} />
          <div className="w-8 h-8 rounded-md border border-border" style={{ backgroundColor: secondaryColor }} />
          <button
            type="button"
            className="px-4 py-1.5 text-sm font-medium text-white rounded-md"
            style={{ backgroundColor: primaryColor }}
          >
            Button
          </button>
        </div>
      </FormBox>

      {/* -- Border Radius ------------------------------------ */}
      <FormBox title="Border Radius" icon={<Square />}>
        {/* Slider */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Rounded corners</span>
            <span className="text-xs font-mono font-semibold text-primary tabular-nums">
              {br.toFixed(2)}rem
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={br}
            onChange={(e) => setValue('borderRadius', parseFloat(e.target.value))}
            className="w-full h-1.5 accent-primary cursor-pointer"
          />
        </div>

        {/* Live preview */}
        <div className="pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground mb-3">Live Preview</p>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              className="px-4 py-1.5 text-sm font-medium text-white transition-all duration-300"
              style={{ backgroundColor: primaryColor, borderRadius: `${br}rem` }}
            >
              Button
            </button>
            <input
              readOnly
              value="Input Field"
              className="px-3 py-1.5 text-sm border border-input bg-background text-foreground outline-none transition-all duration-300"
              style={{ borderRadius: `${br}rem` }}
            />
            <span
              className="px-2.5 py-0.5 text-xs font-medium border transition-all duration-300"
              style={{
                borderRadius: `${br}rem`,
                color: primaryColor,
                borderColor: primaryColor,
                backgroundColor: `${primaryColor}1a`,
              }}
            >
              Badge
            </span>
            <span
              className="px-5 py-2 text-sm font-medium border-2 transition-all duration-300"
              style={{
                borderRadius: `${br}rem`,
                borderColor: primaryColor,
                color: primaryColor,
                backgroundColor: `${primaryColor}15`,
              }}
            >
              Tab
            </span>
            <div
              className="w-10 h-10 border-2 border-border transition-all duration-300"
              style={{ borderRadius: `${br}rem`, backgroundColor: `${primaryColor}0d` }}
            />
          </div>
        </div>
      </FormBox>
    </div>
  );
}
